import {
  Component,
  computed,
  signal,
  HostListener,
  ElementRef,
  inject,
} from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { NgClass } from '@angular/common';
import { PlanFeatureService } from 'src/app/services/plan-feature.service';
import { FEATURES } from 'src/app/shared/config/plan-features';

@Component({
  selector: 'app-header-app',
  imports: [RouterModule, NgClass],
  templateUrl: './header-app.component.html',
  styleUrls: ['./header-app.component.css'],
})
export class HeaderAppComponent {
  private authService = inject(AuthService);
  private onboardingState = inject(OnboardingStateService);
  private elementRef = inject(ElementRef);
  router = inject(Router);
  readonly planFeatureService = inject(PlanFeatureService);
  readonly FEATURES = FEATURES;

  // UI state
  menuOpen = signal(false);
  mobileMenuOpen = signal(false);

  // Router URL as signal (reactive to navigation)
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  // Computed signals from existing service signals
  readonly userEmail = computed(
    () => this.authService.currentUser()?.email ?? '',
  );

  readonly merchantType = computed(() => {
    const onboardingType = this.onboardingState.merchantType();
    if (onboardingType) return onboardingType;
    return this.authService.currentUser()?.merchantType || 'fixed';
  });

  readonly isOnboardingCompleted = computed(
    () => this.authService.currentUser()?.onboardingCompleted || false,
  );

  readonly currentPlan = computed(() => {
    const sub = this.authService.subscription();
    return sub?.plan?.toUpperCase() || 'FREE';
  });

  readonly planLabel = computed(() => {
    const labels: Record<string, string> = {
      FREE: 'Essai',
      STARTER: 'Starter',
      PRO: 'Pro',
      BUSINESS: 'Business',
    };
    return labels[this.currentPlan()] || 'Free';
  });

  readonly planColorClass = computed(() => {
    const colors: Record<string, string> = {
      FREE: 'bg-gray-100 text-gray-600',
      STARTER: 'bg-purple-50 text-purple-700',
      PRO: 'bg-indigo-50 text-indigo-700',
      BUSINESS: 'bg-amber-50 text-amber-700',
    };
    return colors[this.currentPlan()] || 'bg-gray-100 text-gray-600';
  });

  readonly eventsLabel = computed(() =>
    this.merchantType() === 'itinerant' ? 'Événements' : 'Services',
  );

  readonly eventsRoute = computed(() =>
    this.merchantType() === 'itinerant' ? '/events' : '/services',
  );

  // Pre-computed active route states
  readonly isDashboardActive = computed(() =>
    this.currentUrl()?.startsWith('/dashboard'),
  );
  readonly isKioskActive = computed(() =>
    this.currentUrl()?.startsWith('/kiosk'),
  );
  readonly isEventsOrServicesActive = computed(
    () =>
      this.currentUrl()?.startsWith('/events') ||
      this.currentUrl()?.startsWith('/services'),
  );
  readonly isRecommendationsActive = computed(() =>
    this.currentUrl()?.startsWith('/recommendations'),
  );

  // Fermer le menu quand on clique en dehors
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (
      this.menuOpen() &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.menuOpen.set(false);
    }
  }

  toggleMenu() {
    this.menuOpen.update((v) => !v);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update((v) => !v);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  goDashboard() {
    this.router.navigate(['/dashboard']);
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/']);
  }
}
